import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Copy, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';

const AFFILIATE_API_URL = 'https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/affiliateur';

export default function Affiliate() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('affiliate_token'));
  const [affiliateData, setAffiliateData] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) {
      fetchAffiliateData();
      fetchStats();
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${AFFILIATE_API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('affiliate_token', data.token);
        setToken(data.token);
        setAffiliateData(data.affiliate);
        setCurrentPage('dashboard');
      } else {
        setError(data.message || 'Erreur de connexion');
      }
    } catch (err) {
      setError('Erreur réseau: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAffiliateData = async () => {
    try {
      const response = await fetch(`${AFFILIATE_API_URL}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setAffiliateData(data.affiliate);
      }
    } catch (err) {
      console.error('Erreur fetch affiliate:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${AFFILIATE_API_URL}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setStats(data);
      }
    } catch (err) {
      console.error('Erreur fetch stats:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('affiliate_token');
    setToken(null);
    setAffiliateData(null);
    setStats(null);
    setCurrentPage('login');
    setEmail('');
    setPassword('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-black to-slate-950 text-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="border border-white/10 rounded-lg p-8 bg-white/5 backdrop-blur">
            <h1 className="text-3xl font-bold mb-2 text-center">Hiperyon Affiliate</h1>
            <p className="text-slate-400 text-center mb-8">Earn money by sharing Hiperyon</p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>

              {error && <div className="text-red-400 text-sm">{error}</div>}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Log in'}
              </button>
            </form>

            <p className="text-slate-400 text-sm text-center mt-6">
              Don't have an account? Contact the Hiperyon team
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-black to-slate-950 text-slate-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <img src="/logo_svg.svg" alt="Hiperyon Logo" className="h-12 w-12" />
            <h1 className="text-3xl font-bold">Hiperyon Affiliate Dashboard</h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-600/20 border border-red-500/50 rounded-lg hover:bg-red-600/30 transition-colors"
          >
            <LogOut size={18} />
            Log out
          </button>
        </div>

        {/* Navigation */}
        <div className="flex gap-4 mb-8 border-b border-white/10">
          <button
            onClick={() => setCurrentPage('dashboard')}
            className={`px-4 py-2 font-medium transition-colors ${
              currentPage === 'dashboard'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setCurrentPage('account')}
            className={`px-4 py-2 font-medium transition-colors ${
              currentPage === 'account'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            My Account
          </button>
          <button
            onClick={() => setCurrentPage('earnings')}
            className={`px-4 py-2 font-medium transition-colors ${
              currentPage === 'earnings'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            My Balance
          </button>
        </div>

        {/* Dashboard Page */}
        {currentPage === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-white/10 rounded-lg p-6 bg-white/5">
                <p className="text-slate-400 text-sm mb-2">Total signups</p>
                <p className="text-4xl font-bold">{stats.total_signups}</p>
              </div>
              <div className="border border-white/10 rounded-lg p-6 bg-white/5">
                <p className="text-slate-400 text-sm mb-2">Paid conversions</p>
                <p className="text-4xl font-bold">{
                  typeof stats.total_conversions === 'number'
                    ? stats.total_conversions
                    : (Array.isArray(stats.paid_users) && stats.paid_users.length > 0 ? stats.paid_users.length : 0)
                }</p>
              </div>
              <div className="border border-white/10 rounded-lg p-6 bg-white/5">
                <p className="text-slate-400 text-sm mb-2">Conversion rate</p>
                <p className="text-4xl font-bold">{stats.conversion_rate.toFixed(1)}%</p>
              </div>
              <div className="border border-white/10 rounded-lg p-6 bg-white/5">
                <p className="text-slate-400 text-sm mb-2">Affiliate link</p>
                <div className="flex gap-2 mt-2">
                  <code className="text-sm bg-white/10 px-3 py-1 rounded flex-1 truncate">
                    {stats.code_aff ? `hiperyon.com?ref=${stats.code_aff}` : '-'}
                  </code>
                  <button
                    onClick={() => copyToClipboard(stats.code_aff ? `hiperyon.com?ref=${stats.code_aff}` : '')}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                  >
                    {copied ? '✓' : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <div className="border border-white/10 rounded-lg p-6 bg-white/5">
                <p className="text-slate-400 text-sm mb-2">Potential earnings</p>
                <p className="text-4xl font-bold">{typeof stats.paypossible === 'number' ? `${stats.paypossible} €` : '0 €'}</p>
                <p className="text-slate-400 text-xs mt-2">Amount you could earn</p>
              </div>
            </div>

            {/* Paid users */}
            {stats.paid_users && stats.paid_users.length > 0 && (
              <div className="border border-white/10 rounded-lg p-6 bg-white/5">
                <h2 className="text-xl font-bold mb-4">Paying users</h2>
                <div className="space-y-2">
                  {stats.paid_users.map((user, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-white/5 rounded">
                      <div>
                        <p className="font-medium">{user.email}</p>
                        <p className="text-xs text-slate-400">{user.event}</p>
                      </div>
                      <p className="text-sm text-slate-400">{new Date(user.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Account Page */}
        {currentPage === 'account' && affiliateData && (
          <div className="space-y-6 max-w-2xl">
            <div className="border border-white/10 rounded-lg p-6 bg-white/5">
              <h2 className="text-xl font-bold mb-4">Account information</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-slate-400 text-sm">Email</p>
                  <p className="text-lg">{affiliateData.email}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Last name</p>
                  <p className="text-lg">{affiliateData.nom || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">First name</p>
                  <p className="text-lg">{affiliateData.prenom || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Affiliate code</p>
                  <div className="flex gap-2 mt-2">
                    <code className="text-sm bg-white/10 px-3 py-1 rounded flex-1">
                      {affiliateData.code_aff}
                    </code>
                    <button
                      onClick={() => copyToClipboard(affiliateData.code_aff)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                    >
                      {copied ? '✓' : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Earnings Page */}
        {currentPage === 'earnings' && (
          <div className="border border-white/10 rounded-lg p-6 bg-white/5 max-w-2xl">
            <h2 className="text-xl font-bold mb-4">My Balance</h2>
            <div className="space-y-4">
              <div className="text-center p-8 bg-white/5 rounded-lg">
                <p className="text-slate-400 mb-2">Potential Earnings</p>
                <p className="text-4xl font-bold">{stats && typeof stats.paypossible === 'number' ? `${stats.paypossible} €` : '0 €'}</p>
                <p className="text-slate-400 text-xs mt-2">This is the amount you could earn based on your current referrals.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
